import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import './BlogDetail.scss';

interface BlogPost {
  id: number;
  slug: string;
  title: string;
  content: string;
  category: string;
  date: string;
  readTime: string;
  author: string;
  image: string;
}

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  // Sample blog data - in production this would come from an API
  const sampleBlogs: BlogPost[] = [
    {
      id: 1,
      slug: 'the-future-of-web-development-in-2025',
      title: 'The Future of Web Development in 2025',
      content: `
        <h2>Introduction</h2>
        <p>Web development continues to evolve at a rapid pace, with new technologies and frameworks emerging every year. As we look ahead to 2025, several key trends are shaping the future of how we build and deploy web applications.</p>
        
        <h2>Key Trends to Watch</h2>
        <h3>1. AI-Powered Development</h3>
        <p>Artificial intelligence is transforming how developers write code. From intelligent code completion to automated testing, AI tools are becoming essential parts of the modern developer's toolkit.</p>
        
        <h3>2. Edge Computing</h3>
        <p>Moving computation closer to users reduces latency and improves performance. Edge functions and edge databases are becoming mainstream, enabling faster and more responsive applications.</p>
        
        <h3>3. WebAssembly Goes Mainstream</h3>
        <p>WebAssembly continues to gain traction, enabling high-performance applications in the browser. From image processing to gaming, WASM opens new possibilities for web development.</p>
        
        <h2>Conclusion</h2>
        <p>The future of web development is bright, with exciting technologies enabling developers to build faster, more powerful, and more user-friendly applications than ever before.</p>
      `,
      category: 'Web Development',
      date: 'January 15, 2025',
      readTime: '5 min read',
      author: 'App Developer Pro Team',
      image: '/images/blog/web-dev-2025.jpg'
    },
    {
      id: 2,
      slug: 'react-19-whats-new-and-exciting',
      title: "React 19: What's New and Exciting",
      content: `
        <h2>React 19 Overview</h2>
        <p>React 19 brings significant improvements to performance, developer experience, and new features that make building modern applications easier than ever.</p>
        
        <h2>New Features</h2>
        <h3>1. React Compiler</h3>
        <p>The new React Compiler automatically optimizes your components, eliminating the need for manual memoization in many cases.</p>
        
        <h3>2. Server Components</h3>
        <p>React Server Components are now fully stable, enabling better performance and reduced bundle sizes for your applications.</p>
        
        <h3>3. Actions</h3>
        <p>New Actions API simplifies form handling and data mutations, making it easier to build interactive applications.</p>
        
        <h2>Migration Guide</h2>
        <p>Upgrading to React 19 is straightforward for most applications. Check our migration guide for detailed steps.</p>
      `,
      category: 'React',
      date: 'January 10, 2025',
      readTime: '7 min read',
      author: 'App Developer Pro Team',
      image: '/images/blog/react-19.jpg'
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      const foundBlog = sampleBlogs.find(b => b.slug === slug);
      setBlog(foundBlog || null);
      setLoading(false);
    }, 300);
  }, [slug]);

  if (loading) {
    return (
      <div className="blog-detail-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading article...</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="blog-detail-page">
        <div className="not-found">
          <h1>Article Not Found</h1>
          <p>The article you're looking for doesn't exist or has been moved.</p>
          <Link to="/blogs" className="back-link">← Back to Blog</Link>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog.title} | App Developer Pro Blog</title>
        <meta name="description" content={blog.content.substring(0, 160).replace(/<[^>]*>/g, '')} />
        <meta name="keywords" content={`${blog.category}, web development, app developer pro`} />
        <link rel="canonical" href={`https://appdeveloperpro.com/blog/${blog.slug}`} />
      </Helmet>

      <div className="blog-detail-page">
        {/* Hero Section */}
        <motion.section 
          className="blog-hero"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container">
            <Link to="/blogs" className="back-link">← Back to Blog</Link>
            <div className="blog-meta">
              <span className="category">{blog.category}</span>
              <span className="date">{blog.date}</span>
              <span className="read-time">{blog.readTime}</span>
            </div>
            <h1>{blog.title}</h1>
            <p className="author">By {blog.author}</p>
          </div>
        </motion.section>

        {/* Content Section */}
        <motion.section 
          className="blog-content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="container">
            <article 
              className="blog-article"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
            
            {/* Share Section */}
            <div className="share-section">
              <h3>Share this article</h3>
              <div className="share-buttons">
                <button className="share-btn twitter">Twitter</button>
                <button className="share-btn linkedin">LinkedIn</button>
                <button className="share-btn facebook">Facebook</button>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Related Posts */}
        <section className="related-posts">
          <div className="container">
            <h2>Related Articles</h2>
            <div className="posts-grid">
              {sampleBlogs.filter(b => b.id !== blog.id).slice(0, 2).map(post => (
                <article key={post.id} className="post-card">
                  <Link to={`/blog/${post.slug}`}>
                    <div className="post-image">
                      <span>{post.category}</span>
                    </div>
                    <div className="post-content">
                      <h3>{post.title}</h3>
                      <span className="date">{post.date}</span>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>

      <style>{styles}</style>
    </>
  );
};

const styles = `
  .blog-detail-page {
    min-height: 100vh;
  }

  .blog-hero {
    background: linear-gradient(135deg, #0EA5E9 0%, #1E3A5F 100%);
    padding: 120px 0 80px;
    color: white;
  }

  .container {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .back-link {
    display: inline-block;
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    margin-bottom: 30px;
    font-size: 0.9rem;
    transition: color 0.3s ease;
  }

  .back-link:hover {
    color: white;
  }

  .blog-hero .blog-meta {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }

  .blog-hero .category {
    background: rgba(255, 255, 255, 0.2);
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.875rem;
  }

  .blog-hero .date,
  .blog-hero .read-time {
    opacity: 0.8;
    font-size: 0.875rem;
  }

  .blog-hero h1 {
    font-size: 2.5rem;
    font-weight: 700;
    line-height: 1.3;
    margin-bottom: 20px;
  }

  .author {
    opacity: 0.8;
  }

  .blog-content-section {
    padding: 60px 0;
    background: #fff;
  }

  .blog-article {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #333;
  }

  .blog-article h2 {
    font-size: 1.75rem;
    font-weight: 700;
    margin: 40px 0 20px;
    color: #1a1a2e;
  }

  .blog-article h3 {
    font-size: 1.35rem;
    font-weight: 600;
    margin: 30px 0 15px;
    color: #1a1a2e;
  }

  .blog-article p {
    margin-bottom: 20px;
  }

  .share-section {
    margin-top: 60px;
    padding-top: 40px;
    border-top: 1px solid #eee;
    text-align: center;
  }

  .share-section h3 {
    margin-bottom: 20px;
    color: #1a1a2e;
  }

  .share-buttons {
    display: flex;
    gap: 15px;
    justify-content: center;
  }

  .share-btn {
    padding: 10px 24px;
    border: none;
    border-radius: 25px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.3s ease;
  }

  .share-btn:hover {
    transform: translateY(-2px);
  }

  .share-btn.twitter {
    background: #1da1f2;
    color: white;
  }

  .share-btn.linkedin {
    background: #0077b5;
    color: white;
  }

  .share-btn.facebook {
    background: #1877f2;
    color: white;
  }

  .related-posts {
    padding: 80px 0;
    background: #f8f9fa;
  }

  .related-posts h2 {
    text-align: center;
    font-size: 2rem;
    margin-bottom: 40px;
    color: #1a1a2e;
  }

  .posts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 30px;
  }

  .post-card {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }

  .post-card a {
    text-decoration: none;
    color: inherit;
  }

  .post-image {
    height: 150px;
    background: linear-gradient(135deg, #0EA5E9 0%, #1E3A5F 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
  }

  .post-content {
    padding: 20px;
  }

  .post-content h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 10px;
  }

  .post-content .date {
    color: #888;
    font-size: 0.875rem;
  }

  .loading-state,
  .not-found {
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 60px 20px;
  }

  .spinner {
    width: 50px;
    height: 50px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #0EA5E9;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 20px;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .not-found h1 {
    font-size: 2rem;
    color: #1a1a2e;
    margin-bottom: 15px;
  }

  .not-found p {
    color: #666;
    margin-bottom: 30px;
  }

  .not-found .back-link {
    color: #0EA5E9;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    .blog-hero h1 {
      font-size: 1.75rem;
    }

    .blog-article {
      font-size: 1rem;
    }
  }
`;

export default BlogDetail;

